import { getOdooApiHeaders, odooApiFetch } from "app/utils/auth";
import { DriverLocationRecord, DriverOption } from "app/types/driver";

type OdooFilterClause = unknown[];

const serializeOdooFilterAtom = (value: unknown): string => {
  if (value === true) {
    return "True";
  }
  if (value === false) {
    return "False";
  }
  if (value === null || value === undefined) {
    return "None";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(serializeOdooFilterAtom).join(", ")}]`;
  }

  return JSON.stringify(value);
};

export const serializeOdooFilters = (filters: OdooFilterClause[]): string => {
  const clauses = filters.map(
    (clause) => `[${clause.map(serializeOdooFilterAtom).join(", ")}]`
  );

  return `[${clauses.join(", ")}]`;
};

/** Odoo domain: driver OR depot_address partners (matches route/vehicle depot_id domain). */
export const PARTNER_DRIVER_OR_DEPOT_DOMAIN: unknown[] = [
  "|",
  ["driver", "=", true],
  ["depot_address", "=", true],
];

export const PARTNER_DRIVER_OR_DEPOT_INCLUDE_FIELDS =
  "['id','name','driver','depot_address','is_on_duty']";

export const serializeOdooDomain = (domain: unknown[]): string =>
  `[${domain.map(serializeOdooFilterAtom).join(", ")}]`;

export type DeliveryPartner = {
  id: number;
  name: string;
  driver?: boolean;
  depot_address?: boolean;
  is_on_duty?: boolean;
};

export function splitDeliveryPartners(partners: DeliveryPartner[]): {
  drivers: Array<{ id: number; name: string; is_on_duty?: boolean }>;
  depots: Array<{ id: number; name: string }>;
} {
  const drivers: Array<{ id: number; name: string; is_on_duty?: boolean }> = [];
  const depots: Array<{ id: number; name: string }> = [];

  for (const partner of partners) {
    if (!partner?.id || !partner?.name) {
      continue;
    }
    if (partner.driver) {
      drivers.push({
        id: partner.id,
        name: partner.name,
        is_on_duty: Boolean(partner.is_on_duty),
      });
    }
    if (partner.depot_address) {
      depots.push({ id: partner.id, name: partner.name });
    }
  }

  drivers.sort((a, b) => a.name.localeCompare(b.name));
  depots.sort((a, b) => a.name.localeCompare(b.name));

  return { drivers, depots };
}

export function buildDeliveryPartnersUrl(limit = 500): string {
  const params = new URLSearchParams({
    filters: serializeOdooDomain(PARTNER_DRIVER_OR_DEPOT_DOMAIN),
    include_fields: PARTNER_DRIVER_OR_DEPOT_INCLUDE_FIELDS,
    limit: String(limit),
  });

  return `${process.env.REACT_APP_API_BASE_URL}/res.partner/?${params.toString()}`;
}

export async function updatePickingDeliveryArea(
  pickingId: number,
  deliveryAreaId: number | false,
  accessToken: string
): Promise<unknown> {
  return odooApiFetch(
    `${process.env.REACT_APP_API_BASE_URL}/stock.picking/${pickingId}`,
    accessToken,
    {
      method: "PUT",
      headers: getOdooApiHeaders(accessToken, "application/json"),
      body: JSON.stringify({ delivery_area: deliveryAreaId }),
    }
  );
}

export type AssignPickingsPayload = {
  picking_ids: number[];
  driver_id: number;
  route_id?: number;
  create_new_route?: boolean;
  date?: string;
  start_time?: string;
  finish_time?: string;
  depot_id?: number;
  is_round_trip?: boolean;
};

export type AssignPickingsResponse = {
  message?: string;
  route_id?: number;
  error?: string;
  error_descrip?: string;
};

export async function assignPickingsToRoute(
  payload: AssignPickingsPayload,
  accessToken: string
): Promise<AssignPickingsResponse> {
  return odooApiFetch<AssignPickingsResponse>(
    `${process.env.REACT_APP_API_BASE_URL}/delivery.route/assign_pickings`,
    accessToken,
    {
      method: "POST",
      headers: getOdooApiHeaders(accessToken, "application/json"),
      body: JSON.stringify(payload),
    }
  );
}

export type ReassignPickingsResponse = AssignPickingsResponse;

export async function reassignPickingsToRoute(
  payload: AssignPickingsPayload,
  accessToken: string
): Promise<ReassignPickingsResponse> {
  return odooApiFetch<ReassignPickingsResponse>(
    `${process.env.REACT_APP_API_BASE_URL}/delivery.route/reassign_pickings`,
    accessToken,
    {
      method: "POST",
      headers: getOdooApiHeaders(accessToken, "application/json"),
      body: JSON.stringify(payload),
    }
  );
}

export type ReleasePickingResponse = {
  message?: string;
  picking_id?: number;
  picking_name?: string;
  route_id?: number | false;
  driver_id?: number | false;
  stop_id?: number;
  stop_is_active?: boolean;
  released_at?: string | false;
  error?: string;
  error_descrip?: string;
};

export async function releasePickingForReassignment(
  pickingId: number,
  accessToken: string,
  options?: { clearDriver?: boolean }
): Promise<ReleasePickingResponse> {
  return odooApiFetch<ReleasePickingResponse>(
    `${process.env.REACT_APP_API_BASE_URL}/delivery.route/release_picking`,
    accessToken,
    {
      method: "POST",
      headers: getOdooApiHeaders(accessToken, "application/json"),
      body: JSON.stringify({
        picking_id: pickingId,
        clear_driver: options?.clearDriver ?? true,
      }),
    }
  );
}

/** Routes eligible for assign / reassign picker (draft and assigned only). */
export const ASSIGNABLE_ROUTE_STATE_FILTER: unknown[][] = [
  ["state", "in", ["draft", "assigned"]],
];

export function buildAssignableRouteFilters(options?: {
  fromDate?: string;
  toDate?: string;
}): unknown[][] {
  const filters: unknown[][] = [...ASSIGNABLE_ROUTE_STATE_FILTER];
  if (options?.fromDate) {
    filters.push(["date", ">=", options.fromDate]);
  }
  if (options?.toDate) {
    filters.push(["date", "<=", options.toDate]);
  }
  return filters;
}

export const extractOdooResults = <T = unknown>(parsed: unknown): T[] => {
  if (Array.isArray(parsed)) {
    return parsed as T[];
  }

  if (!parsed || typeof parsed !== "object") {
    return [];
  }

  const results = (parsed as { results?: unknown }).results;
  return Array.isArray(results) ? (results as T[]) : [];
};

export async function fetchDriverLocations(
  accessToken: string
): Promise<DriverLocationRecord[]> {
  const parsed = await odooApiFetch<{ drivers?: DriverLocationRecord[] }>(
    `${process.env.REACT_APP_API_BASE_URL}/api/drivers/locations`,
    accessToken
  );
  return Array.isArray(parsed.drivers) ? parsed.drivers : [];
}

export function mergeDriverLocations(
  drivers: DriverOption[],
  locations: DriverLocationRecord[]
): DriverOption[] {
  const locationById = new Map(locations.map((location) => [location.id, location]));

  return drivers.map((driver) => {
    const location = locationById.get(driver.id);
    if (!location) {
      return driver;
    }

    const lastLat =
      typeof location.last_lat === "number" && Number.isFinite(location.last_lat)
        ? location.last_lat
        : null;
    const lastLng =
      typeof location.last_lng === "number" && Number.isFinite(location.last_lng)
        ? location.last_lng
        : null;

    return {
      ...driver,
      last_lat: lastLat,
      last_lng: lastLng,
      last_ping_at:
        typeof location.last_ping_at === "string" ? location.last_ping_at : null,
    };
  });
}
